import React from "react";
import { getFloorPriceReachedBasis } from "./ItemListColumns";

interface ItemListFiltersProps {
    filterCompareSite: string;
    onFilterChange: (compareSite: string) => void;
    disabledCompareSites: Partial<Record<'danawa' | 'enuri' | 'naver', true>> | null;
    filterItemStatus: ItemStatusFilter;
    onStatusChange: (status: ItemStatusFilter) => void;
    filterFloorReached: boolean;
    onFloorReachedChange: (reached: boolean) => void;
    filterKeepDefaultPrice: KeepDefaultPriceFilter;
    onKeepDefaultPriceChange: (value: KeepDefaultPriceFilter) => void;
    filterCompareRange: CompareRangeFilter;
    onCompareRangeChange: (value: CompareRangeFilter) => void;
    filterMarketSite: MarketSiteFilter;
    onMarketSiteChange: (value: MarketSiteFilter) => void;
    filterAccumulatedError: AccumulatedErrorFilter;
    onAccumulatedErrorChange: (value: AccumulatedErrorFilter) => void;
    items: any[];
}

export type ItemStatusFilter = '' | 'active' | 'inactive';
export type KeepDefaultPriceFilter = '' | '1' | '0';
export type CompareRangeFilter = '' | 'items_all' | 'items_same_market' | 'items_specified';
export type MarketSiteFilter = '' | 'smartstore' | 'auction' | 'gmarket' | 'eleventh' | 'lotteon' | 'ssgdotcom';
export type AccumulatedErrorFilter = '' | '1' | '0';

export const normalizeMarketSiteFilter = (value: string | null): MarketSiteFilter =>
    value === 'smartstore' || value === 'auction' || value === 'gmarket' || value === 'eleventh' || value === 'lotteon' || value === 'ssgdotcom' ? value : '';

export const normalizeItemStatusFilter = (value: string | null): ItemStatusFilter => {
    if (value === 'active' || value === 'inactive') {
        return value;
    }
    return '';
};

export const normalizeKeepDefaultPriceFilter = (value: string | null): KeepDefaultPriceFilter =>
    value === '1' || value === '0' ? value : '';

export const normalizeCompareRangeFilter = (value: string | null): CompareRangeFilter =>
    value === 'items_all' || value === 'items_same_market' || value === 'items_specified' ? value : '';

export const normalizeAccumulatedErrorFilter = (value: string | null): AccumulatedErrorFilter =>
    value === '1' || value === '0' ? value : '';

export const filterItemListItems = (
    items: any[], status: ItemStatusFilter, floorReached: boolean,
    keepDefaultPrice: KeepDefaultPriceFilter = '', compareRange: CompareRangeFilter = '',
    marketSite: MarketSiteFilter = '', accumulatedError: AccumulatedErrorFilter = '',
) =>
    items.filter((item) => {
        if (status === 'active' && !item.is_active) return false;
        if (status === 'inactive' && item.is_active) return false;
        if (keepDefaultPrice === '1' && !item.keep_default_price) return false;
        if (keepDefaultPrice === '0' && item.keep_default_price) return false;
        if (compareRange && item.compare_range !== compareRange) return false;
        if (marketSite && item.shop?.market_site !== marketSite) return false;
        if (accumulatedError === '1' && !(item.error_count > 0)) return false;
        if (accumulatedError === '0' && item.error_count > 0) return false;
        return !floorReached || getFloorPriceReachedBasis(item) !== null;
    });

export const ItemListFilters: React.FC<ItemListFiltersProps> = ({
    filterCompareSite,
    onFilterChange,
    disabledCompareSites,
    filterItemStatus,
    onStatusChange,
    filterFloorReached,
    onFloorReachedChange,
    filterKeepDefaultPrice,
    onKeepDefaultPriceChange,
    filterCompareRange,
    onCompareRangeChange,
    filterMarketSite,
    onMarketSiteChange,
    filterAccumulatedError,
    onAccumulatedErrorChange,
}) => {
    // 비교사이트별 카운트 UI는 주석 처리됨. 필요 시 props.items로 다시 계산.
    return (
        <div className="item-list-filters">
            <ul className="nav nav-tabs">
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${filterCompareSite === '' ? 'active' : ''}`}
                        onClick={() => onFilterChange('')}
                    >
                        전체
                        {/* <small className="d-none d-sm-inline-block">({compareSiteCounts.total})</small> */}
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${filterCompareSite === 'danawa' ? 'active' : ''}`}
                        onClick={() => onFilterChange('danawa')}
                        aria-label={disabledCompareSites?.danawa ? '다나와 (자동 처리 비활성)' : undefined}
                        title={disabledCompareSites?.danawa ? '자동 처리 비활성' : undefined}
                    >
                        <span className={disabledCompareSites?.danawa ? 'compare-site-processing-disabled' : undefined}>다나와</span>
                        {/* <small className="d-none d-sm-inline-block">({compareSiteCounts.danawa})</small> */}
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${filterCompareSite === 'enuri' ? 'active' : ''}`}
                        onClick={() => onFilterChange('enuri')}
                        aria-label={disabledCompareSites?.enuri ? '에누리 (자동 처리 비활성)' : undefined}
                        title={disabledCompareSites?.enuri ? '자동 처리 비활성' : undefined}
                    >
                        <span className={disabledCompareSites?.enuri ? 'compare-site-processing-disabled' : undefined}>에누리</span>
                        {/* <small className="d-none d-sm-inline-block">({compareSiteCounts.enuri})</small> */}
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${filterCompareSite === 'naver' ? 'active' : ''}`}
                        onClick={() => onFilterChange('naver')}
                        aria-label={disabledCompareSites?.naver ? '네이버 (자동 처리 비활성)' : undefined}
                        title={disabledCompareSites?.naver ? '자동 처리 비활성' : undefined}
                    >
                        <span className={disabledCompareSites?.naver ? 'compare-site-processing-disabled' : undefined}>네이버</span>
                        {/* <small className="d-none d-sm-inline-block">({compareSiteCounts.naver})</small> */}
                        {/* <small className="badge text-bg-secondary ms-2 d-none d-sm-inline-block">준비중</small> */}
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${filterCompareSite === 'direct' ? 'active' : ''}`}
                        onClick={() => onFilterChange('direct')}
                    >
                        직접비교
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${filterCompareSite === 'coupang' ? 'active' : ''}`}
                        onClick={() => onFilterChange('coupang')}
                        disabled={true}
                    >
                        쿠팡
                        {/* <small className="d-none d-sm-inline-block">({compareSiteCounts.coupang})</small> */}
                        <small className="badge text-bg-secondary ms-2 d-none d-sm-inline-block">준비중</small>
                    </button>
                </li>
            </ul>
            <div className="item-list-status-filter">
                <select
                    id="item-market-site-filter"
                    className="form-select form-select-sm"
                    aria-label="상점"
                    value={filterMarketSite}
                    onChange={(event) => onMarketSiteChange(normalizeMarketSiteFilter(event.target.value))}
                >
                    <option value="">상점</option>
                    <option value="smartstore">스마트스토어</option>
                    <option value="auction">옥션</option>
                    <option value="gmarket">지마켓</option>
                    <option value="eleventh">11번가</option>
                    <option value="lotteon">롯데온</option>
                    <option value="ssgdotcom">SSG닷컴</option>
                </select>
                <select
                    id="item-active-filter"
                    className="form-select form-select-sm"
                    aria-label="활성 상태"
                    value={filterItemStatus}
                    onChange={(event) => onStatusChange(normalizeItemStatusFilter(event.target.value))}
                >
                    <option value="">활성</option>
                    <option value="active">활성 상품</option>
                    <option value="inactive">비활성 상품</option>
                </select>
                <select
                    id="item-restore-filter"
                    className="form-select form-select-sm"
                    aria-label="복구가"
                    value={filterKeepDefaultPrice}
                    onChange={(event) => onKeepDefaultPriceChange(normalizeKeepDefaultPriceFilter(event.target.value))}
                >
                    <option value="">복구가</option>
                    <option value="1">복구가 유지</option>
                    <option value="0">복구가 유지해제</option>
                </select>
                <select
                    id="item-compare-range-filter"
                    className="form-select form-select-sm"
                    aria-label="비교범위"
                    value={filterCompareRange}
                    onChange={(event) => onCompareRangeChange(normalizeCompareRangeFilter(event.target.value))}
                >
                    <option value="">비교범위</option>
                    <option value="items_all">전체상품</option>
                    <option value="items_same_market">동일마켓</option>
                    <option value="items_specified">지정상품</option>
                </select>
                <select
                    id="item-floor-filter"
                    className="form-select form-select-sm"
                    aria-label="하한가"
                    value={filterFloorReached ? 'reached' : ''}
                    onChange={(event) => onFloorReachedChange(event.target.value === 'reached')}
                >
                    <option value="">하한가</option>
                    <option value="reached">하한가 도달</option>
                </select>
                <select
                    id="item-accumulated-error-filter"
                    className="form-select form-select-sm"
                    aria-label="에러누적"
                    value={filterAccumulatedError}
                    onChange={(event) => onAccumulatedErrorChange(normalizeAccumulatedErrorFilter(event.target.value))}
                >
                    <option value="">에러누적</option>
                    <option value="1">에러누적 있음</option>
                    <option value="0">에러누적 없음</option>
                </select>
            </div>
        </div>
    );
};

export default ItemListFilters;
